import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import {
  CheckIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import AttributeLabel from './AttributeLabel';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface Trainer {
  id: string;
  mg_nr?: string;
  member_id?: string;
  vorname: string;
  name: string;
}

interface EditEventModalProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
  refreshCalendar: () => void;
}

// Available colors matching design spec
const colorOptions = [
  '#5eb900', // Gruen
  '#F6BF26', // Gelb
  '#D50000', // Rot
  '#F4511E', // Orange
  '#E67C73', // Rosa
  '#33B679', // Tuerkis
  '#039BE5', // Blau
];

const EditEventModal: React.FC<EditEventModalProps> = ({ event, isOpen, onClose, refreshCalendar }) => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    color: '#5eb900',
    bookable: 3,
    trainer_id: '',
    co_trainer_id: '',
    start_time: '08:00',
    end_time: '09:00',
    taetigkeitsbericht: false,
    taetigkeitsberichtText: '',
  });
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalFormData, setOriginalFormData] = useState<any>(null);

  useEffect(() => {
    if (isOpen && event) {
      const startTime = event?.start ? format(event.start, 'HH:mm') : '08:00';
      const endTime = event?.end ? format(event.end, 'HH:mm') : '09:00';

      const initialData = {
        color: event?.extendedProps?.color || colorOptions[0],
        bookable: event?.extendedProps?.bookable || 3,
        trainer_id: event?.extendedProps?.trainer_id || event?.extendedProps?.creator?.id || '',
        co_trainer_id: event?.extendedProps?.co_trainer_id || event?.extendedProps?.cotrainer?.id || '',
        start_time: startTime,
        end_time: endTime,
        taetigkeitsbericht: event?.extendedProps?.taetigkeitsbericht === 1 || event?.extendedProps?.taetigkeitsbericht === true,
        taetigkeitsberichtText: event?.extendedProps?.taetigkeitsbericht_text || '',
      };

      setFormData(initialData);
      setOriginalFormData(initialData);
    }
  }, [isOpen, event]);

  useEffect(() => {
    const fetchTrainers = async () => {
      if (!isOpen) return;

      try {
        const response = await apiClient.get('/api/members/trainer');
        const mappedTrainers = response.data.map((trainer: any) => {
          const nameParts = trainer.name.trim().split(' ');
          const vorname = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : '';
          const name = nameParts.length > 1 ? nameParts[nameParts.length - 1] : trainer.name;
          return {
            id: trainer.mg_nr || trainer.member_id,
            mg_nr: trainer.mg_nr,
            member_id: trainer.member_id,
            vorname,
            name,
          };
        });
        setTrainers(mappedTrainers);
      } catch (err) {
        console.error('Error fetching trainers:', err);
      }
    };

    fetchTrainers();
  }, [isOpen]);

  const hasChanges = () => {
    if (!originalFormData) return false;

    return (
      formData.color !== originalFormData.color ||
      formData.bookable !== originalFormData.bookable ||
      formData.trainer_id !== originalFormData.trainer_id ||
      formData.co_trainer_id !== originalFormData.co_trainer_id ||
      formData.start_time !== originalFormData.start_time ||
      formData.end_time !== originalFormData.end_time ||
      formData.taetigkeitsbericht !== originalFormData.taetigkeitsbericht ||
      formData.taetigkeitsberichtText !== originalFormData.taetigkeitsberichtText
    );
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!hasChanges()) {
      setError('Keine Aenderungen vorgenommen');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!formData.trainer_id) {
      setError('Bitte wählen Sie einen Trainer aus');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.patch(`/api/calendar/event/${event.id}`, {
        color: formData.color,
        bookable: formData.bookable,
        trainer_id: formData.trainer_id,
        co_trainer_id: formData.co_trainer_id || null,
        start_time: formData.start_time,
        end_time: formData.end_time,
        taetigkeitsbericht: formData.taetigkeitsbericht ? 1 : 0,
        taetigkeitsbericht_text: formData.taetigkeitsbericht ? formData.taetigkeitsberichtText : null,
      });

      if (response.data.success) {
        refreshCalendar();
        onClose();
      } else {
        setError(response.data.message || 'Fehler beim Bearbeiten des Termins');
      }
    } catch (err: any) {
      setError(`Fehler beim Bearbeiten: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  const currentParticipants = event?.extendedProps?.bookingCount || 0;

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      variant="form"
      size="md"
      title={event?.title || 'Termin bearbeiten'}
      subtitle="Termin bearbeiten"
      icon={PencilIcon}
      closeOnBackdrop={false}
      footer={
        <ModalActions
          secondary={{ label: 'Abbrechen', onClick: onClose }}
          primary={{
            label: 'Speichern',
            onClick: () => handleSubmit(),
            loading,
            disabled: loading || !hasChanges(),
            icon: <CheckIcon className="w-5 h-5" />,
          }}
        />
      }
    >
      <div className="space-y-5">
        {/* Error Message */}
        {error && (
          <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 text-error-700 dark:text-error-300 rounded-xl text-sm flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* DATUM Section - Locked */}
        <ModalSection className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5" />
            Datum
          </h4>
          <div className="relative">
            <div className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <span className="flex-1 text-gray-500 dark:text-gray-400 font-medium">
                {event?.start ? format(event.start, 'EEEE, dd. MMMM yyyy', { locale: de }) : 'Datum unbekannt'}
              </span>
              <div className="flex items-center gap-1">
                <LockClosedIcon className="w-4 h-4 text-gray-400" />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1">
              <ExclamationTriangleIcon className="w-3.5 h-3.5" />
              Datum kann nicht geaendert werden
            </p>
          </div>
        </ModalSection>

        {/* ZEITRAUM Section */}
        <ModalSection className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <ClockIcon className="w-3.5 h-3.5" />
            Zeitraum
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <AttributeLabel iconKey="time" size="sm" className="block mb-1" textClassName="text-xs text-gray-500 dark:text-gray-400">Von</AttributeLabel>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div>
              <AttributeLabel iconKey="time" size="sm" className="block mb-1" textClassName="text-xs text-gray-500 dark:text-gray-400">Bis</AttributeLabel>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>
        </ModalSection>

        {/* TRAINER Section */}
        <ModalSection className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <UserIcon className="w-3.5 h-3.5" />
            Trainer
          </h4>
          <select
            value={formData.trainer_id}
            onChange={(e) => setFormData({ ...formData, trainer_id: e.target.value })}
            className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="">Trainer auswaehlen</option>
            {trainers.map((trainer) => (
              <option key={trainer.id} value={trainer.id}>
                {trainer.vorname} {trainer.name}
              </option>
            ))}
          </select>
        </ModalSection>

        {/* CO-TRAINER Section */}
        <ModalSection className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <UsersIcon className="w-3.5 h-3.5" />
            Co-Trainer
          </h4>
          <select
            value={formData.co_trainer_id}
            onChange={(e) => setFormData({ ...formData, co_trainer_id: e.target.value })}
            className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="">Kein Co-Trainer</option>
            {trainers.map((trainer) => (
              <option key={trainer.id} value={trainer.id}>
                {trainer.vorname} {trainer.name}
              </option>
            ))}
          </select>
        </ModalSection>

        {/* KAPAZITAET Section */}
        <ModalSection className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <UserGroupIcon className="w-3.5 h-3.5" />
            Kapazitaet
          </h4>
          <div>
            <AttributeLabel iconKey="capacity" size="sm" className="block mb-1" textClassName="text-xs text-gray-500 dark:text-gray-400">Max. Teilnehmer</AttributeLabel>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={String(formData.bookable)}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setFormData({ ...formData, bookable: val === '' ? 0 : parseInt(val) });
              }}
              className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {formData.bookable === 0 ? 'Termin nicht buchbar' : `${formData.bookable} Teilnehmer`}
            </p>
            {currentParticipants > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                {currentParticipants} Buchungen bereits vorhanden
              </p>
            )}
          </div>
        </ModalSection>

        {/* TÄTIGKEITSBERICHT Section */}
        <ModalSection className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            Tätigkeitsbericht
          </h4>
          <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="edit-taetigkeitsbericht"
                checked={formData.taetigkeitsbericht}
                onChange={(e) => setFormData({ ...formData, taetigkeitsbericht: e.target.checked })}
                className="w-4 h-4 text-amber-500 border-gray-300 dark:border-gray-600 rounded focus:ring-amber-500"
              />
              <label htmlFor="edit-taetigkeitsbericht" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Im Tätigkeitsbericht erwähnen
              </label>
            </div>
            {formData.taetigkeitsbericht && (
              <div className="mt-3">
                <AttributeLabel iconKey="description" size="sm" className="block mb-1" textClassName="text-xs text-gray-500 dark:text-gray-400">
                  Beschreibung für Tätigkeitsbericht
                </AttributeLabel>
                <textarea
                  value={formData.taetigkeitsberichtText}
                  onChange={(e) => setFormData({ ...formData, taetigkeitsberichtText: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  rows={2}
                  placeholder="z.B. Jahreshauptversammlung mit Vorstandswahlen"
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.taetigkeitsberichtText.length}/500 Zeichen
                </p>
              </div>
            )}
          </div>
        </ModalSection>

        {/* FARBE Section */}
        <ModalSection className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            Farbe
          </h4>
          <div className="flex justify-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
            {colorOptions.map((color) => (
              <button
                key={color}
                onClick={() => setFormData({ ...formData, color })}
                className={`w-10 h-10 rounded-full transition-all hover:scale-110 ${
                  formData.color === color ? 'ring-2 ring-offset-2 ring-gray-800 dark:ring-white' : ''
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </ModalSection>
      </div>
    </ModalShell>
  );
};

export default EditEventModal;
